import React, { useState, useEffect, useMemo } from 'react';
import styles from '../../../styles/ReportsDashboard.module.css';
import type { ListingSummary } from '../../../types/ListingTypes';
import StatusBadge from './StatusBadge';

interface ListingReportsProps {
    userDetails: any;
    listings: any[];
    listingsLoading: boolean;
}

const AgencyListingReports: React.FC<ListingReportsProps> = ({ userDetails, listings, listingsLoading }) => {
    const saleCount = listings.filter(l => l.type === 'Sale').length;
    const rentalCount = listings.filter(l => l.type === 'Rental').length;

    const formatAddress = (l: ListingSummary) =>
        `${l.streetNumber} ${l.streetName}, ${l.city}`;

    const statusCounts = useMemo(
        () => ({
            Open: listings.filter(l => l.status === 'Open').length,
            Pending: listings.filter(l => l.status === 'Pending').length,
            Closed: listings.filter(l => l.status === 'Closed').length,
            Incomplete: listings.filter(l => l.status === 'Incomplete').length,
        }),
        [listings]
    );

    return (
        <div>
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                    🏠 Listings (Agency)
                </h2>
                {listingsLoading ? (
                    <p className={styles.loading}>Loading listings…</p>
                ) : (
                    <>
                        <div className={styles.statsRow}>
                            <div className={styles.statCard}>
                                <span className={styles.statValue} style={{ color: '#2563eb' }}>
                                    {listings.length}
                                </span>
                                <span className={styles.statLabel}>Total</span>
                            </div>
                            {(Object.entries(statusCounts) as [string, number][]).map(([status, count]) => (
                                <div key={status} className={styles.statCard}>
                                    <span className={styles.statValue}>{count}</span>
                                    <span className={styles.statLabel}>{status}</span>
                                </div>
                            ))}
                            <div className={styles.statCard}>
                                <span className={styles.statValue}>{saleCount}</span>
                                <span className={styles.statLabel}>Sales</span>
                            </div>
                            <div className={styles.statCard}>
                                <span className={styles.statValue}>{rentalCount}</span>
                                <span className={styles.statLabel}>Rentals</span>
                            </div>
                        </div>

                        {listings.length > 0 ? (
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Address</th>
                                        <th>Type</th>
                                        <th>Status</th>
                                        <th>Agent</th>
                                        <th>Created</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listings.map(l => (
                                        <tr key={l.id}>
                                            <td>{formatAddress(l)}</td>
                                            <td>{l.type}</td>
                                            <td>
                                                <StatusBadge status={l.status} />
                                            </td>
                                            <td>
                                                {l.agent
                                                    ? `${l.agent.fullNames} ${l.agent.lastName}`
                                                    : '—'}
                                            </td>
                                            <td>{new Date(l.createdAt).toLocaleDateString('en-ZA')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p className={styles.empty}>No listings found.</p>
                        )}
                    </>
                )}
            </section>
        </div>
    )
}

export default AgencyListingReports;